(function () {
  "use strict";

  var GLOBAL_KEY = "SilkSilkyCartCoordinator";
  var CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
  ].join(", ");
  var SYSTEM_GIFT_PROPERTY = "_ss_free_gift_campaign";
  var CHECKOUT_PREPARE_TIMEOUT_MS = 8000;

  if (window[GLOBAL_KEY] && window[GLOBAL_KEY].__theme === true) return;

  var checkoutSteps = new Map();
  var scheduledMutations = new Map();
  var pendingOperations = new Set();
  var mutationOutcomes = [];
  var mutationOutcomeSequence = 0;
  var idleWaiters = new Set();
  var mutationQueue = Promise.resolve();
  var checkoutPromise = null;
  var navigating = false;

  var api = {
    __theme: true,
    enqueueMutation: enqueueMutation,
    registerCheckoutStep: registerCheckoutStep,
    scheduleMutation: scheduleMutation,
    startCheckout: startCheckout,
    track: track,
    waitForIdle: waitForIdle,
    buyerCartSnapshot: buyerCartSnapshot,
  };

  window[GLOBAL_KEY] = api;
  observeCartFetches();
  window.addEventListener("pageshow", handlePageShow);
  document.addEventListener("click", handleCheckoutClick, true);
  document.addEventListener("submit", handleCheckoutSubmit, true);

  function handlePageShow(event) {
    if (!navigating && !(event && event.persisted)) return;
    navigating = false;
    checkoutPromise = null;
    setCheckoutBusy(false);
  }

  function registerCheckoutStep(name, handler, priority) {
    if (!name || typeof handler !== "function") return function () {};
    checkoutSteps.set(String(name), {
      handler: handler,
      priority: Number(priority) || 0,
    });
    return function () {
      checkoutSteps.delete(String(name));
    };
  }

  function track(operation, name) {
    var promise = Promise.resolve(operation);
    var operationName = name ? String(name) : "";
    pendingOperations.add(promise);
    promise.then(
      function () {
        pendingOperations.delete(promise);
        recordMutationOutcome(operationName, null);
      },
      function (error) {
        pendingOperations.delete(promise);
        recordMutationOutcome(operationName, error);
      }
    );
    return promise;
  }

  function recordMutationOutcome(operationName, error) {
    if (!operationName) return;
    mutationOutcomeSequence += 1;
    mutationOutcomes.push({
      sequence: mutationOutcomeSequence,
      operationName: operationName,
      error: error,
    });
    pruneMutationOutcomes();
  }

  function pruneMutationOutcomes() {
    if (!idleWaiters.size) {
      mutationOutcomes = [];
      return;
    }
    var earliestSequence = Math.min.apply(
      null,
      Array.from(idleWaiters).map(function (waiter) {
        return waiter.afterSequence;
      })
    );
    mutationOutcomes = mutationOutcomes.filter(function (outcome) {
      return outcome.sequence > earliestSequence;
    });
  }

  function enqueueMutation(name, task) {
    var operationName = String(name || "cart-mutation");
    var run = mutationQueue
      .catch(function () {})
      .then(function () {
        return task();
      });
    mutationQueue = run;
    return track(run, operationName);
  }

  function scheduleMutation(name, task, delay) {
    var operationName = String(name || "cart-mutation");
    var entry = scheduledMutations.get(operationName);
    if (!entry) {
      entry = { task: task, timer: 0, waiters: [] };
      scheduledMutations.set(operationName, entry);
    }
    entry.task = task;
    window.clearTimeout(entry.timer);

    var scheduled = new Promise(function (resolve, reject) {
      entry.waiters.push({ resolve: resolve, reject: reject });
    });
    entry.timer = window.setTimeout(function () {
      runScheduledMutation(operationName);
    }, Math.max(0, Number(delay) || 0));
    return scheduled;
  }

  function runScheduledMutation(name) {
    var entry = scheduledMutations.get(name);
    if (!entry) return Promise.resolve();
    scheduledMutations.delete(name);
    window.clearTimeout(entry.timer);
    var operation = enqueueMutation(name, entry.task);
    operation.then(
      function (value) {
        entry.waiters.forEach(function (waiter) {
          waiter.resolve(value);
        });
      },
      function (error) {
        entry.waiters.forEach(function (waiter) {
          waiter.reject(error);
        });
      }
    );
    return operation;
  }

  async function flushScheduledMutations() {
    var names = Array.from(scheduledMutations.keys());
    if (!names.length) return;
    await Promise.allSettled(names.map(runScheduledMutation));
  }

  async function waitForIdle() {
    var waiter = { afterSequence: mutationOutcomeSequence };
    idleWaiters.add(waiter);
    try {
      await flushScheduledMutations();
      while (pendingOperations.size || scheduledMutations.size) {
        if (pendingOperations.size) {
          await Promise.allSettled(Array.from(pendingOperations));
        }
        await flushScheduledMutations();
      }
      var latestOutcomes = new Map();
      mutationOutcomes.forEach(function (outcome) {
        if (outcome.sequence > waiter.afterSequence) {
          latestOutcomes.set(outcome.operationName, outcome);
        }
      });
      var failedOutcome = Array.from(latestOutcomes.values()).find(function (
        outcome
      ) {
        return Boolean(outcome.error);
      });
      if (failedOutcome) throw failedOutcome.error;
    } finally {
      idleWaiters.delete(waiter);
      pruneMutationOutcomes();
    }
  }

  function handleCheckoutClick(event) {
    var target = event.target instanceof Element ? event.target : null;
    var trigger = target && target.closest(CHECKOUT_SELECTOR);
    if (!isCartCheckoutTrigger(trigger) || !isUsableCheckoutTrigger(trigger)) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    startCheckout(trigger);
  }

  function isCartCheckoutTrigger(trigger) {
    if (!trigger) return false;
    var formId = trigger.getAttribute("form");
    if (formId === "cart" || formId === "minicart") return true;
    var form = typeof trigger.closest === "function" && trigger.closest("form");
    return Boolean(form && (form.id === "cart" || form.id === "minicart"));
  }

  function handleCheckoutSubmit(event) {
    var form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || (form.id !== "minicart" && form.id !== "cart")) return;
    var submitter = event.submitter instanceof Element ? event.submitter : null;
    var trigger =
      (submitter && submitter.closest(CHECKOUT_SELECTOR)) ||
      document.querySelector('[form="' + form.id + '"][name="checkout"]') ||
      form.querySelector(CHECKOUT_SELECTOR);
    if (!isUsableCheckoutTrigger(trigger)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    startCheckout(trigger);
  }

  function isUsableCheckoutTrigger(trigger) {
    return Boolean(
      trigger &&
        !navigating &&
        !trigger.disabled &&
        trigger.getAttribute("aria-disabled") !== "true"
    );
  }

  function observeCartFetches() {
    if (typeof window.fetch !== "function" || window.fetch.__ssCartCoordinated) {
      return;
    }
    var originalFetch = window.fetch.bind(window);
    var coordinatedFetch = function (input, init) {
      if (!isCartMutationRequest(input, init)) {
        return originalFetch(input, init);
      }
      if (isInternalCartMutation(init)) {
        return track(originalFetch(input, init), "cart-network");
      }
      return enqueueMutation("cart-network", function () {
        return originalFetch(input, init);
      });
    };
    coordinatedFetch.__ssCartCoordinated = true;
    coordinatedFetch.__ssCartOriginalFetch = originalFetch;
    window.fetch = coordinatedFetch;
  }

  function isInternalCartMutation(init) {
    return Boolean(
      init &&
        (init.ssCartCoordinatorInternal || init.ssCartPromotionInternal)
    );
  }

  function isCartMutationRequest(input, init) {
    var rawUrl =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
        ? input.url
        : "";
    if (!rawUrl) return false;
    var method = String(
      (init && init.method) || (input && input.method) || "GET"
    ).toUpperCase();
    if (method === "GET" || method === "HEAD") return false;
    try {
      var pathname = new URL(rawUrl, window.location.origin).pathname;
      return /(?:^|\/)cart\/(?:add|change|update|clear)(?:\.(?:js|json))?$/.test(
        pathname
      );
    } catch (_error) {
      return false;
    }
  }

  function startCheckout(trigger) {
    if (checkoutPromise) return checkoutPromise;
    checkoutPromise = prepareCheckoutWithinDeadline(trigger)
      .catch(function (error) {
        if (isCheckoutBlockingError(error)) {
          showCheckoutError(error);
          return false;
        }
        console.warn(
          "Unable to prepare cart for checkout; continuing to Shopify checkout",
          error
        );
        return navigateToCheckout(trigger);
      })
      .finally(function () {
        if (!navigating) {
          setCheckoutBusy(false);
          checkoutPromise = null;
        }
      });
    return checkoutPromise;
  }

  function prepareCheckoutWithinDeadline(trigger) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        console.warn(
          "Cart preparation timed out; continuing to Shopify checkout"
        );
        resolve(navigateToCheckout(trigger));
      }, CHECKOUT_PREPARE_TIMEOUT_MS);

      prepareCheckout(trigger).then(
        function (value) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve(value);
        },
        function (error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  async function prepareCheckout(trigger) {
    setCheckoutBusy(true);
    await waitForIdle();
    var beforeCart = await fetchCart();
    var steps = Array.from(checkoutSteps.entries()).sort(function (left, right) {
      return left[1].priority - right[1].priority;
    });

    for (var index = 0; index < steps.length; index += 1) {
      await steps[index][1].handler({
        trigger: trigger,
        cart: beforeCart,
        coordinator: api,
      });
    }

    await waitForIdle();
    var afterCart = await fetchCart();
    if (buyerCartSnapshot(beforeCart) !== buyerCartSnapshot(afterCart)) {
      throw buyerCartIntegrityError();
    }

    return navigateToCheckout(trigger);
  }

  function navigateToCheckout(trigger) {
    if (navigating) return true;
    navigating = true;
    var destination =
      trigger && trigger.tagName === "A" && trigger.href
        ? trigger.href
        : storefrontPath("checkout");
    window.location.assign(destination);
    return true;
  }

  function buyerCartIntegrityError() {
    var error = new Error(cartErrorMessage());
    error.code = "SS_BUYER_CART_INTEGRITY";
    return error;
  }

  function isBuyerCartIntegrityError(error) {
    return Boolean(error && error.code === "SS_BUYER_CART_INTEGRITY");
  }

  function isCheckoutBlockingError(error) {
    return Boolean(
      isBuyerCartIntegrityError(error) ||
        (error && error.code === "SS_CART_PREPARATION_USER_ERROR")
    );
  }

  function setCheckoutBusy(isBusy) {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (trigger) {
      if (isBusy) {
        trigger.dataset.ssCheckoutWasDisabled = trigger.disabled ? "true" : "false";
        trigger.setAttribute("aria-busy", "true");
        if ("disabled" in trigger) trigger.disabled = true;
      } else {
        trigger.removeAttribute("aria-busy");
        if (
          "disabled" in trigger &&
          trigger.dataset.ssCheckoutWasDisabled !== "true"
        ) {
          trigger.disabled = false;
        }
        delete trigger.dataset.ssCheckoutWasDisabled;
      }
    });
  }

  async function fetchCart() {
    var response = await fetch(storefrontPath("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(cartErrorMessage());
    return response.json();
  }

  function buyerCartSnapshot(cart) {
    var quantities = new Map();
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      if (isSystemGift(item)) return;
      var identity = buyerLineIdentity(item);
      quantities.set(identity, (quantities.get(identity) || 0) + Number(item.quantity || 0));
    });
    return JSON.stringify(
      Array.from(quantities.entries()).sort(function (left, right) {
        return left[0].localeCompare(right[0]);
      })
    );
  }

  function buyerLineIdentity(item) {
    var properties = item && item.properties ? item.properties : {};
    var propertyEntries = Object.keys(properties)
      .sort()
      .map(function (key) {
        return [key, String(properties[key] == null ? "" : properties[key])];
      });
    var sellingPlan =
      item && item.selling_plan_allocation
        ? item.selling_plan_allocation.selling_plan &&
          item.selling_plan_allocation.selling_plan.id
          ? item.selling_plan_allocation.selling_plan.id
          : item.selling_plan_allocation.selling_plan_id || ""
        : "";
    return JSON.stringify([
      String((item && (item.variant_id || item.id)) || ""),
      String(sellingPlan || ""),
      propertyEntries,
    ]);
  }

  function isSystemGift(item) {
    return Boolean(
      item && item.properties && item.properties[SYSTEM_GIFT_PROPERTY]
    );
  }

  function storefrontPath(path) {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";
    var normalizedRoot = String(root || "/");
    if (normalizedRoot.charAt(normalizedRoot.length - 1) !== "/") {
      normalizedRoot += "/";
    }
    return normalizedRoot + String(path || "").replace(/^\/+/, "");
  }

  function cartErrorMessage() {
    return window.cartStrings && window.cartStrings.error
      ? window.cartStrings.error
      : "Unable to update the cart.";
  }

  function showCheckoutError(error) {
    var message =
      error && error.message ? String(error.message) : cartErrorMessage();
    document.dispatchEvent(
      new CustomEvent("silksilky:cart-checkout:error", {
        detail: { error: error, message: message },
      })
    );
    var cartErrors = document.getElementById("cart-errors");
    if (cartErrors) {
      cartErrors.textContent = message;
      cartErrors.setAttribute("role", "alert");
    }
    if (typeof window.showToast === "function") {
      var wrapper = document.createElement("div");
      wrapper.className = "form__message error";
      wrapper.textContent = message;
      window.showToast(wrapper.outerHTML, 5000, "modal-error");
    }
    console.error("Unable to prepare cart for checkout", error);
  }
})();
